import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (pw !== pw2) { toast.error('Passwords do not match'); return; }
    if (!token) { toast.error('Missing reset token'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset_password', { token, new_password: pw });
      toast.success('Password reset. You are signed out of all devices — please log in.');
      nav('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed — the link may have expired');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E11] grid place-items-center p-6">
      <div className="w-full max-w-md border border-[#2B3139] bg-[#181A20] p-8 rounded-sm">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[#FCD535]" />
          <h1 className="font-display text-white text-2xl font-bold tracking-tight">Set a new password</h1>
        </div>
        <p className="text-[#848E9C] text-sm mb-6">
          Choose a strong password. All existing sessions will be signed out after reset.
        </p>
        {!token && (
          <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-3 rounded-sm text-sm text-[#F6465D] mb-4" data-testid="reset-no-token">
            Reset token missing. Request a new link from the <Link to="/forgot-password" className="underline">forgot-password</Link> page.
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">New password</Label>
            <div className="relative mt-1.5">
              <Input type={show ? 'text' : 'password'} value={pw} onChange={e=>setPw(e.target.value)} required
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] pr-10"
                data-testid="reset-pw-input" />
              <button type="button" onClick={()=>setShow(v=>!v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#848E9C] hover:text-white" data-testid="reset-toggle-show">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Confirm password</Label>
            <Input type={show ? 'text' : 'password'} value={pw2} onChange={e=>setPw2(e.target.value)} required
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] mt-1.5"
              data-testid="reset-pw2-input" />
          </div>
          <Button type="submit" disabled={loading || !token}
            className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold h-11 rounded-sm"
            data-testid="reset-submit-btn">
            {loading ? 'Resetting…' : 'Reset password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
